import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { TfiAngleLeft, TfiSearch } from "react-icons/tfi";
import axios from "axios";
import { useNavContext } from "../context/nav_context";
import { Loading } from "../components";
import {
  api_v3_get_moderators,
  api_v3_list_of_players_admin,
  api_v3_update_player_roles,
} from "../utils/constants";

const ModManagementPage = () => {
  const { user } = useNavContext();
  const [isLoading, setIsLoading] = useState(true);
  const [moderators, setModerators] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMods = async () => {
      setIsLoading(true);
      try {
        const { data } = await axios.get(api_v3_get_moderators());
        setModerators(data);
        setError(null);
      } catch (err) {
        console.error("Error fetching moderators:", err);
        setError("Error al cargar los moderadores");
      } finally { setIsLoading(false); }
    };
    fetchMods();
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    try {
      const { data } = await axios.get(api_v3_list_of_players_admin(null));
      const filtered = data.filter(
        (p) => p.username.toLowerCase().includes(searchQuery.toLowerCase()) && !moderators.some((mod) => mod.playerId === p.playerId)
      );
      setSearchResults(filtered);
      setError(null);
    } catch (err) {
      console.error("Error searching players:", err);
      setError("Error al buscar jugadores");
    }
  };

  const addModerator = async (player) => {
    try {
      await axios.put(api_v3_update_player_roles(player.playerId), { isAdmin: player.isAdmin, isModerator: true });
      setModerators([...moderators, player]);
      setSearchResults(searchResults.filter((p) => p.playerId !== player.playerId));
      setError(null);
    } catch (err) {
      console.error("Error adding moderator:", err);
      setError("Error al añadir moderador");
    }
  };

  const removeModerator = async (player) => {
    try {
      await axios.put(api_v3_update_player_roles(player.playerId), { isAdmin: player.isAdmin, isModerator: false });
      setModerators(moderators.filter((mod) => mod.playerId !== player.playerId));
      setError(null);
    } catch (err) {
      console.error("Error removing moderator:", err);
      setError("Error al quitar moderador");
    }
  };

  if (!user || !user.isAdmin) return <Canvas><EmptyState>Lo siento, pero estás intentando acceder a donde no debes.</EmptyState></Canvas>;
  if (isLoading) return <Canvas><Loading /></Canvas>;

  return (
    <Canvas>
      <Content>
        <HeaderRow>
          <PageTitle>MODERADORES</PageTitle>
          <BackLink to="/admin/"><TfiAngleLeft /> ADMIN HOME</BackLink>
        </HeaderRow>

        {error && <AlertBox>{error}</AlertBox>}

        <SectionCard>
          <SectionTitle>AÑADIR MODERADOR</SectionTitle>
          <SearchRow>
            <SearchInput
              placeholder="Buscar por username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
            />
            <SearchBtn onClick={handleSearch}><TfiSearch /> BUSCAR</SearchBtn>
          </SearchRow>
          {searchResults.length > 0 && (
            <ResultList>
              {searchResults.map((player) => (
                <PlayerRow key={player.playerId}>
                  <PlayerName>{player.username}</PlayerName>
                  <AddModBtn onClick={() => addModerator(player)}>AÑADIR</AddModBtn>
                </PlayerRow>
              ))}
            </ResultList>
          )}
        </SectionCard>

        <SectionCard>
          <SectionTitle>MODERADORES ACTUALES</SectionTitle>
          {moderators.length === 0 ? (
            <MutedText>No hay moderadores configurados.</MutedText>
          ) : (
            <ModList>
              {moderators.map((mod) => (
                <PlayerRow key={mod.playerId}>
                  <PlayerName>{mod.username}</PlayerName>
                  <RemoveBtn onClick={() => removeModerator(mod)}>QUITAR</RemoveBtn>
                </PlayerRow>
              ))}
            </ModList>
          )}
        </SectionCard>
      </Content>
    </Canvas>
  );
};

const Canvas = styled.div`background: var(--v-canvas); min-height: 100vh; display: flex; justify-content: center; padding: 4rem 1.5rem;`;
const Content = styled.div`width: 100%; max-width: 640px;`;
const PageTitle = styled.h1`font-family: Anton, sans-serif; font-size: clamp(36px, 6vw, 56px); font-weight: 400; text-transform: uppercase; line-height: 1; color: var(--v-fg); margin: 0;`;
const EmptyState = styled.p`font-family: var(--v-font-body); font-size: 14px; color: var(--v-fg-muted); text-align: center; padding: 4rem 1rem;`;
const HeaderRow = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 2.5rem; flex-wrap: wrap; gap: 1rem;`;
const BackLink = styled(Link)`display: inline-flex; align-items: center; gap: 0.4rem; font-family: var(--v-font-mono); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--v-mint); text-decoration: none; padding: 0.5rem 0.9rem; border: 1px solid rgba(60,255,208,0.2); border-radius: 24px; transition: background 0.15s ease; &:hover { background: rgba(60,255,208,0.04); }`;
const AlertBox = styled.div`background: rgba(255,59,59,0.1); border: 1px solid rgba(255,59,59,0.2); color: #ff3b3b; font-family: var(--v-font-mono); font-size: 11px; padding: 0.6rem 0.85rem; border-radius: 10px; margin-bottom: 1rem;`;
const SectionCard = styled.div`background: var(--v-surface); border: 1px solid rgba(255,255,255,0.06); border-radius: 20px; padding: 1.5rem; margin-bottom: 1.25rem;`;
const SectionTitle = styled.h3`font-family: var(--v-font-mono); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: var(--v-fg-muted); margin: 0 0 1rem;`;
const SearchRow = styled.div`display: flex; gap: 0.5rem; margin-bottom: 1rem;`;
const SearchInput = styled.input`flex: 1; padding: 0.6rem 0.85rem; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; color: var(--v-fg); font-family: var(--v-font-body); font-size: 13px; outline: none; transition: border-color 0.15s ease; &:focus { border-color: var(--v-mint); } &::placeholder { color: var(--v-fg-muted); opacity: 0.4; }`;
const SearchBtn = styled.button`display: inline-flex; align-items: center; gap: 0.4rem; background: var(--v-mint); border: none; color: #000; font-family: var(--v-font-mono); font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.5rem 1rem; border-radius: 20px; cursor: pointer; transition: opacity 0.15s ease; &:hover { opacity: 0.85; }`;
const ResultList = styled.div`background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); border-radius: 14px; overflow: hidden;`;
const ModList = styled.div`background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); border-radius: 14px; overflow: hidden;`;
const PlayerRow = styled.div`display: flex; justify-content: space-between; align-items: center; padding: 0.6rem 0.85rem; border-bottom: 1px solid rgba(255,255,255,0.04); &:last-child { border-bottom: none; }`;
const PlayerName = styled.span`font-family: var(--v-font-body); font-size: 14px; font-weight: 600; color: var(--v-fg);`;
const MutedText = styled.p`font-family: var(--v-font-body); font-size: 13px; color: var(--v-fg-muted); margin: 0;`;
const AddModBtn = styled.button`background: var(--v-mint); border: none; color: #000; font-family: var(--v-font-mono); font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.35rem 0.85rem; border-radius: 16px; cursor: pointer; transition: opacity 0.15s ease; &:hover { opacity: 0.85; }`;
const RemoveBtn = styled.button`background: transparent; border: 1px solid rgba(255,59,59,0.3); color: #ff3b3b; font-family: var(--v-font-mono); font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.35rem 0.85rem; border-radius: 16px; cursor: pointer; transition: background 0.15s ease; &:hover { background: rgba(255,59,59,0.06); }`;

export default ModManagementPage;
